/* PropBuilds brick guide viewer (propbuilds-minibricks v3). One module for every brick guide page: it reads the model
   from the section's data-model URL, drives the 3D build station, and upgrades the server-rendered parts list. The page
   text, step list and parts table are already in the HTML, so the page reads fine if this never runs. */
import * as THREE from "https://cdn.jsdelivr.net/npm/three@0.170.0/+esm";
import { OrbitControls } from "https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/controls/OrbitControls.js/+esm";
import { toCreasedNormals } from "https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/utils/BufferGeometryUtils.js/+esm";

const ROOT = document.querySelector("[data-pb-bg]");
if (ROOT) boot(ROOT).catch((e) => fail(ROOT, e));

function fail(root, e) {
  const s = root.querySelector("[data-bg-status]");
  if (s) s.textContent = "The 3D model couldn't load. The steps and the parts list below still work.";
  const bar = root.querySelector("[data-bg-bar]");
  if (bar) bar.parentElement.hidden = true;
  if (window.console) console.warn("brick guide:", e);
}

function webglOK() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGL2RenderingContext && c.getContext("webgl2")) || !!c.getContext("webgl");
  } catch (e) { return false; }
}

async function loadModel(url, onProgress) {
  const r = await fetch(url, { credentials: "omit" });
  if (!r.ok) throw new Error("model " + r.status);
  const total = +r.headers.get("content-length") || 0;
  if (!r.body || !window.TextDecoder) return r.json();
  const reader = r.body.getReader();
  const chunks = [];
  let got = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    got += value.length;
    onProgress(total ? got / total : 0, got);
  }
  const all = new Uint8Array(got);
  let o = 0;
  for (const c of chunks) { all.set(c, o); o += c.length; }
  return JSON.parse(new TextDecoder().decode(all));
}

async function boot(root) {
  const $ = (s) => root.querySelector(s);
  const $$ = (s) => [...root.querySelectorAll(s)];
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const slug = root.dataset.slug;
  const status = $("[data-bg-status]");
  const bar = $("[data-bg-bar]");
  const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const mem = {
    get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* storage unavailable */ } },
  };

  const M = await loadModel(root.dataset.model, (f, got) => {
    if (bar) bar.style.width = f ? Math.round(f * 100) + "%" : "40%";
    if (status) status.textContent = "Loading the 3D model... " + (f ? Math.round(f * 100) + "%" : Math.round(got / 1024) + " KB");
  });
  const PD = M.parts_def, COL = M.colors;
  const nSteps = M.steps.length;
  const KEY = `pbguide:${slug}:step`;
  const store = {
    get() { const v = +mem.get(KEY); return v >= 1 && v <= nSteps ? v : 0; },
    set(v) { mem.set(KEY, String(v)); },
  };

  // ---------------------------------------------------------------- per-part lookups
  const stepBag = M.steps.map((s) => s.bag);
  const subIndex = Object.fromEntries(Object.keys(M.subs || {}).map((n, i) => [n, i]));
  const partsInStep = Array.from({ length: nSteps }, () => []);
  M.parts.forEach((p, i) => partsInStep[p[6]].push(i));
  const bagParts = M.bags.map(() => 0);
  M.parts.forEach((p) => bagParts[stepBag[p[6]]]++);
  const checkpoints = new Set(M.checkpoints || []);
  const placedBefore = new Array(nSteps + 1).fill(0);
  for (let k = 0; k < nSteps; k++) placedBefore[k + 1] = placedBefore[k] + partsInStep[k].length;

  // ---------------------------------------------------------------- parts list (works without WebGL)
  let bomBag = -1;
  function bomFor(bag) {
    const m = new Map();
    M.parts.forEach((p) => { if (bag < 0 || stepBag[p[6]] === bag) { const k = p[0] + "/" + p[1]; m.set(k, (m.get(k) || 0) + 1); } });
    return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }
  const csvFor = (bag) => "Part,Color,Quantity\n" + bomFor(bag).map(([key, q]) => { const [n, c] = key.split("/"); return `${n},${c},${q}`; }).join("\n") + "\n";
  let thumb = null;                                              // set once WebGL is up
  const io = "IntersectionObserver" in window ? new IntersectionObserver((ents) => ents.forEach((e) => {
    if (e.isIntersecting && thumb) { const [n, c] = e.target.dataset.k.split("/"); e.target.src = thumb(n, +c); io.unobserve(e.target); }
  }), { rootMargin: "200px" }) : null;
  const observeThumbs = () => { if (io && thumb) $$("[data-bg-bomrows] img[data-k]:not([src])").forEach((im) => io.observe(im)); };
  function renderBom() {
    const rows = bomFor(bomBag);
    const n = rows.reduce((t, r) => t + r[1], 0);
    $("[data-bg-bomrows]").innerHTML = rows.map(([key, q]) => {
      const [num, cid] = key.split("/");
      return `<tr><td><img alt="" width="42" height="42" data-k="${num}/${cid}"></td><td>${esc(PD[num].name)}</td><td class="pb-bg__n">${esc(num)}</td>` +
        `<td><span class="pb-bg__cc"><span class="pb-bg__sw" style="background:${COL[cid].hex}"></span>${esc(COL[cid].name)}</span></td><td class="pb-bg__q">${q}</td></tr>`;
    }).join("");
    const scope = bomBag < 0 ? "the whole model" : `bag ${bomBag + 1}, ${M.bags[bomBag].name}`;
    $("[data-bg-bomsub]").textContent = `${n.toLocaleString()} pieces in ${rows.length} part and colour combinations for ${scope}.`;
    $$("[data-bg-bomchips] button").forEach((b) => b.setAttribute("aria-pressed", +b.dataset.bag === bomBag ? "true" : "false"));
    observeThumbs();
  }
  $("[data-bg-bomchips]").innerHTML = `<button type="button" data-bag="-1">All</button>` +
    M.bags.map((b, i) => `<button type="button" data-bag="${i}" title="${esc(b.name)}">Bag ${i + 1}</button>`).join("");
  $$("[data-bg-bomchips] button").forEach((b) => b.onclick = () => { bomBag = +b.dataset.bag; renderBom(); });
  renderBom();
  const copyBtn = $("[data-bg-copycsv]");
  if (copyBtn) copyBtn.onclick = async () => {
    const csv = csvFor(bomBag), box = $("[data-bg-csvbox]");
    try { await navigator.clipboard.writeText(csv); copyBtn.textContent = "Copied"; setTimeout(() => (copyBtn.textContent = "Copy as CSV"), 1800); box.hidden = true; }
    catch (e) { box.hidden = false; box.value = csv; box.focus(); box.select(); copyBtn.textContent = "Select and copy below"; }
  };
  $$("[data-bg-csv]").forEach((a) => a.addEventListener("click", (e) => {
    if (a.dataset.bgCsv !== "bag" || bomBag < 0) return;       // the static link already holds the whole list
    e.preventDefault();
    const url = URL.createObjectURL(new Blob([csvFor(bomBag)], { type: "text/csv" }));
    const t = document.createElement("a");
    t.href = url; t.download = `${root.dataset.file || slug}-parts-bag-${bomBag + 1}.csv`;
    document.body.appendChild(t); t.click(); t.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }));

  if (!webglOK()) {
    if (status) status.textContent = "Your browser can't show the 3D viewer. The steps and the parts list below still work.";
    if (bar) bar.parentElement.hidden = true;
    return;
  }

  // ---------------------------------------------------------------- geometry from LDraw meshes
  function f32(b64) {
    const bin = atob(b64);
    const u8 = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
    return new Float32Array(u8.buffer);
  }
  const GEO = {};
  for (const [num, g] of Object.entries(M.geo)) {
    const groups = [];
    for (const [code, b64] of Object.entries(g.tris)) {
      const pos = f32(b64);
      if (!pos.length) continue;
      let geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      geo = toCreasedNormals(geo, Math.PI / 6);
      groups.push({ code: +code, geo, fixed: g.fixed[code] || null });
    }
    const lg = new THREE.BufferGeometry();
    lg.setAttribute("position", new THREE.BufferAttribute(f32(g.lines), 3));
    GEO[num] = { groups, lines: lg };
  }
  // world units: 1 stud = 1, a plate = 0.4 (LDraw 20 LDU = 1 stud); y up, z toward the viewer (south)
  function partMatrix(num, x, y, z, rot) {
    const d = PD[num];
    const L = new THREE.Matrix4().set(1 / 20, 0, 0, -d.lx0 / 20, 0, -1 / 20, 0, d.ybot / 20, 0, 0, -1 / 20, d.lz1 / 20, 0, 0, 0, 1);
    const r = ((rot % 4) + 4) % 4, w = d.w, dd = d.d;
    const R = new THREE.Matrix4();
    if (r === 1) R.set(0, 0, 1, 0, 0, 1, 0, 0, -1, 0, 0, w, 0, 0, 0, 1);
    if (r === 2) R.set(-1, 0, 0, w, 0, 1, 0, 0, 0, 0, -1, dd, 0, 0, 0, 1);
    if (r === 3) R.set(0, 0, -1, dd, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1);
    return new THREE.Matrix4().makeTranslation(x, z * 0.4, y).multiply(R).multiply(L);
  }
  const SUBM = {};
  for (const [name, s] of Object.entries(M.subs || {})) {
    if (!s.angle && !s.yaw) continue;
    const P = new THREE.Vector3(s.pivot[0], s.pivot[2] * 0.4, s.pivot[1]);
    const ax = s.axis === "x" ? new THREE.Vector3(1, 0, 0) : s.axis === "y" ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
    let R = new THREE.Matrix4().makeRotationAxis(ax, THREE.MathUtils.degToRad(s.angle || 0));
    if (s.yaw) R = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 1, 0), THREE.MathUtils.degToRad(s.yaw)).multiply(R);
    SUBM[name] = new THREE.Matrix4().makeTranslation(P.x, P.y, P.z)
      .multiply(R)
      .multiply(new THREE.Matrix4().makeTranslation(-P.x, -P.y, -P.z));
  }
  const partBox = M.parts.map(([num, c, x, y, z, rot, s, sub]) => {
    const d = PD[num];
    const w = rot % 2 ? d.d : d.w, dd = rot % 2 ? d.w : d.d;
    const b = new THREE.Box3();
    const sm = sub && SUBM[sub];
    for (const cx of [x, x + w]) for (const cy of [z * 0.4, (z + d.h) * 0.4]) for (const cz of [y, y + dd]) {
      const v = new THREE.Vector3(cx, cy, cz);
      if (sm) v.applyMatrix4(sm);
      b.expandByPoint(v);
    }
    return b;
  });
  const modelBox = new THREE.Box3();
  partBox.forEach((b) => modelBox.union(b));
  const stepBox = partsInStep.map((ids) => { const b = new THREE.Box3(); ids.forEach((i) => b.union(partBox[i])); return b; });

  // ---------------------------------------------------------------- scene
  const stage = $("[data-bg-stage]");
  const canvas = $("[data-bg-canvas]");
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" });
  const FULL_DPR = Math.min(window.devicePixelRatio || 1, 2);
  const MOVE_DPR = Math.min(FULL_DPR, 1);                // while the view moves; full sharpness again once it settles
  renderer.setPixelRatio(FULL_DPR);
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(getComputedStyle(root).getPropertyValue("--bg-sheet").trim() || "#e8e2d6");
  const camera = new THREE.PerspectiveCamera(30, 1, 0.5, 5000);
  scene.add(new THREE.HemisphereLight(0xffffff, 0x8f8778, 1.6));
  const sun = new THREE.DirectionalLight(0xffffff, 1.9);
  sun.position.set(-0.6, 1.4, 0.9);
  scene.add(sun);
  const fill = new THREE.DirectionalLight(0xffffff, 0.55);
  fill.position.set(1, 0.3, -0.7);
  scene.add(fill);

  const U = { uStep: { value: nSteps }, uFocus: { value: 0 }, uIso: { value: -1 } };
  function stepMaterial(colorHex) {
    const mat = new THREE.MeshStandardMaterial({ color: colorHex || 0xffffff, roughness: 0.42, metalness: 0.0, side: THREE.DoubleSide,
                                                 polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 });
    mat.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, U);
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", "#include <common>\nattribute float aStep;\nattribute float aSub;\nuniform float uIso;\nvarying float vStep;")
        .replace("#include <begin_vertex>", "#include <begin_vertex>\nvStep = aStep;")
        .replace("#include <project_vertex>", "#include <project_vertex>\nif (uIso > -0.5 && abs(aSub - uIso) > 0.5) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);");
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", "#include <common>\nuniform float uStep; uniform float uFocus; varying float vStep;")
        .replace("#include <dithering_fragment>",
          "#include <dithering_fragment>\nif (uFocus > 0.5 && vStep < uStep - 0.5) { gl_FragColor.rgb = mix(gl_FragColor.rgb, vec3(dot(gl_FragColor.rgb, vec3(0.33))), 0.4) * 0.9 + 0.07; }");
    };
    return mat;
  }
  const lineMat = new THREE.ShaderMaterial({
    uniforms: U,
    vertexShader: `attribute vec4 i0; attribute vec4 i1; attribute vec4 i2; attribute vec4 i3; attribute float aStep; attribute float aSub;
      uniform float uStep; uniform float uIso; varying float vShow;
      void main() { mat4 im = mat4(i0, i1, i2, i3); vShow = aStep <= uStep + 0.5 ? 1.0 : 0.0;
        if (uIso > -0.5 && abs(aSub - uIso) > 0.5) vShow = 0.0;
        gl_Position = projectionMatrix * modelViewMatrix * im * vec4(position, 1.0); }`,
    fragmentShader: `varying float vShow; void main() { if (vShow < 0.5) discard; gl_FragColor = vec4(0.1, 0.09, 0.07, 0.55); }`,
    transparent: true, depthWrite: false,
  });

  // one instanced mesh per part and see-through level, so a part made in both solid and clear colours renders each right
  const seeThrough = (cid) => {
    const c = COL[cid];
    if (!c.trans) return "o";
    const h = parseInt(c.hex.slice(1), 16);
    return ((h >> 16) & 255) > 0xd8 && ((h >> 8) & 255) > 0xd8 && (h & 255) > 0xd8 ? "c" : "t";
  };
  const meshes = [];
  const byNum = {};
  M.parts.forEach((p, idx) => { const k = p[0] + "|" + seeThrough(p[1]); (byNum[k] = byNum[k] || []).push(idx); });
  for (const [key, list] of Object.entries(byNum)) {
    const [num, level] = key.split("|");
    const G = GEO[num];
    if (!G) continue;
    list.sort((a, b) => M.parts[a][6] - M.parts[b][6]);
    const mats = list.map((idx) => {
      const [n, c, x, y, z, rot, s, sub] = M.parts[idx];
      const m = partMatrix(n, x, y, z, rot);
      return sub && SUBM[sub] ? SUBM[sub].clone().multiply(m) : m;
    });
    const steps = new Float32Array(list.map((idx) => M.parts[idx][6] + 1));
    const subs = new Float32Array(list.map((idx) => (M.parts[idx][7] ? subIndex[M.parts[idx][7]] : -1)));
    for (const grp of G.groups) {
      const byColor = grp.code === 16;
      const mat = stepMaterial(byColor ? 0xffffff : grp.fixed);
      const g2 = grp.geo.clone();
      g2.setAttribute("aStep", new THREE.InstancedBufferAttribute(steps, 1));
      g2.setAttribute("aSub", new THREE.InstancedBufferAttribute(subs, 1));
      const im = new THREE.InstancedMesh(g2, mat, list.length);
      list.forEach((idx, k) => {
        im.setMatrixAt(k, mats[k]);
        if (byColor) im.setColorAt(k, new THREE.Color(COL[M.parts[idx][1]].hex));
      });
      if (byColor && level !== "o") { mat.transparent = true; mat.opacity = level === "c" ? 0.28 : 0.62; }
      im.instanceMatrix.needsUpdate = true;
      if (im.instanceColor) im.instanceColor.needsUpdate = true;
      im.userData = { steps: Array.from(steps) };
      im.frustumCulled = false;
      scene.add(im);
      meshes.push(im);
    }
    if ((M.meta.no_lines || []).includes(num)) continue;
    const lg = new THREE.InstancedBufferGeometry();
    lg.setAttribute("position", G.lines.getAttribute("position"));
    const cols = [0, 1, 2, 3].map(() => new Float32Array(list.length * 4));
    mats.forEach((m, k) => { const e = m.elements; for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) cols[c][k * 4 + r] = e[c * 4 + r]; });
    cols.forEach((arr, c) => lg.setAttribute("i" + c, new THREE.InstancedBufferAttribute(arr, 4)));
    lg.setAttribute("aStep", new THREE.InstancedBufferAttribute(steps, 1));
    lg.setAttribute("aSub", new THREE.InstancedBufferAttribute(subs, 1));
    lg.instanceCount = list.length;
    const ls = new THREE.LineSegments(lg, lineMat);
    ls.frustumCulled = false;
    ls.userData = { steps: Array.from(steps), lines: true };
    scene.add(ls);
    meshes.push(ls);
  }
  function setCounts(k) {
    for (const m of meshes) {
      const st = m.userData.steps;
      let lo = 0, hi = st.length;
      while (lo < hi) { const mid = (lo + hi) >> 1; if (st[mid] <= k) lo = mid + 1; else hi = mid; }
      if (m.isInstancedMesh) m.count = lo;
    }
    U.uStep.value = k;
  }

  // ---------------------------------------------------------------- camera and on-demand rendering
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = !REDUCED;
  // one frame in flight at a time: OrbitControls fires "change" from inside update(), so a frame must not queue another
  let raf = 0, dirty = true, anim = null, inFrame = false, moving = false, settle = 0, slow = 0, lastT = 0;
  const showLines = (on) => { for (const m of meshes) if (m.isLineSegments) m.visible = on; };
  function frame(now) {
    raf = 0;
    inFrame = true;
    const t = now || performance.now();
    const flying = stepAnim(t);
    const changed = controls.update();
    if (dirty || flying || changed) {
      renderer.render(scene, camera);
      dirty = false;
      if (moving && lastT) {                         // slow frames while moving: drop the edge lines until it settles
        slow = t - lastT > 40 ? slow + 1 : Math.max(0, slow - 1);
        if (slow > 6) showLines(false);
      }
      lastT = moving ? t : 0;
    }
    inFrame = false;
    if ((flying || changed) && !raf) raf = requestAnimationFrame(frame);
  }
  function requestRender() {
    dirty = true;
    if (!inFrame && !raf) raf = requestAnimationFrame(frame);
  }
  function motion(on) {
    clearTimeout(settle);
    if (on) { moving = true; if (renderer.getPixelRatio() !== MOVE_DPR) renderer.setPixelRatio(MOVE_DPR); return; }
    settle = setTimeout(() => {
      moving = false; slow = 0; lastT = 0; showLines(true);
      if (renderer.getPixelRatio() !== FULL_DPR) renderer.setPixelRatio(FULL_DPR);
      requestRender();
    }, 300);
  }
  controls.addEventListener("start", () => motion(true));
  controls.addEventListener("end", () => motion(false));
  controls.addEventListener("change", requestRender);
  const DIRS = {
    front: new THREE.Vector3(-0.42, 0.62, 1.0), back: new THREE.Vector3(0.42, 0.62, -1.0),
    west: new THREE.Vector3(-1.0, 0.62, 0.42), east: new THREE.Vector3(1.0, 0.62, 0.42),
  };
  function viewFor(box, whole) {
    const mc = modelBox.getCenter(new THREE.Vector3()), ms = modelBox.getSize(new THREE.Vector3());
    const c = box.getCenter(new THREE.Vector3()), s = box.getSize(new THREE.Vector3());
    let dir = DIRS.front;
    if (!whole) {
      const vx = (c.x - mc.x) / (ms.x / 2 || 1), vz = (c.z - mc.z) / (ms.z / 2 || 1);
      if (Math.max(Math.abs(vx), Math.abs(vz)) > 0.45) {
        if (Math.abs(vz) * 1.25 >= Math.abs(vx)) dir = vz >= 0 ? DIRS.front : DIRS.back;
        else dir = vx >= 0 ? DIRS.east : DIRS.west;
      }
    }
    const size = Math.max(s.x, s.y, s.z, whole ? 0 : 14);
    const fov = THREE.MathUtils.degToRad(camera.fov);
    const aspectFix = Math.max(1, 1.15 / camera.aspect);
    const dist = (size * (whole ? 0.5 : 0.62) / Math.tan(fov / 2)) * aspectFix + (whole ? 0 : 6);
    return { target: c, pos: c.clone().add(dir.clone().normalize().multiplyScalar(dist)), near: Math.max(0.2, dist / 200), far: dist * 20 };
  }
  function flyTo(v, instant) {
    camera.near = v.near; camera.far = v.far; camera.updateProjectionMatrix();
    if (instant || REDUCED) {
      anim = null; camera.position.copy(v.pos); controls.target.copy(v.target); controls.update(); requestRender(); return;
    }
    anim = { t0: performance.now(), p0: camera.position.clone(), q0: controls.target.clone(), v };
    motion(true);
    requestRender();
  }
  function stepAnim(now) {
    if (!anim) return false;
    const t = Math.min(1, ((now || performance.now()) - anim.t0) / 480), e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    camera.position.lerpVectors(anim.p0, anim.v.pos, e);
    controls.target.lerpVectors(anim.q0, anim.v.target, e);
    if (t >= 1) { anim = null; motion(false); }
    return true;
  }

  // ---------------------------------------------------------------- part thumbnails
  const thumbs = {};
  const trender = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  trender.setSize(128, 128);
  thumb = function (num, cid) {
    const key = num + "/" + cid;
    if (thumbs[key]) return thumbs[key];
    const G = GEO[num];
    const sc = new THREE.Scene();
    sc.add(new THREE.HemisphereLight(0xffffff, 0x8f8778, 1.7));
    const l = new THREE.DirectionalLight(0xffffff, 1.9); l.position.set(-0.6, 1.4, 0.9); sc.add(l);
    const g = new THREE.Group();
    for (const grp of G.groups) {
      const col = grp.code === 16 ? COL[cid].hex : grp.fixed;
      g.add(new THREE.Mesh(grp.geo, new THREE.MeshStandardMaterial({ color: col, roughness: 0.42, transparent: grp.code === 16 && COL[cid].trans,
        opacity: 0.65, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 })));
    }
    g.add(new THREE.LineSegments(G.lines, new THREE.LineBasicMaterial({ color: 0x1a1612 })));
    g.rotation.x = Math.PI;
    sc.add(g);
    const box = new THREE.Box3().setFromObject(g);
    const c = box.getCenter(new THREE.Vector3());
    const r = box.getSize(new THREE.Vector3()).length();
    const cam = new THREE.PerspectiveCamera(26, 1, r / 50, r * 10);
    cam.position.set(c.x - r * 0.9, c.y + r * 0.8, c.z + r * 1.3);
    cam.lookAt(c);
    trender.render(sc, cam);
    thumbs[key] = trender.domElement.toDataURL("image/png");
    return thumbs[key];
  };
  observeThumbs();

  // ---------------------------------------------------------------- station
  let cur = 0;
  const slider = $("[data-bg-slider]");
  slider.max = nSteps;
  const rail = $("[data-bg-rail]");
  rail.innerHTML = M.bags.map((b, i) => `<button type="button" data-bag="${i}" style="flex-grow:${bagParts[i]}" title="Bag ${i + 1}: ${esc(b.name)}"><span>${i + 1}</span></button>`).join("");
  rail.querySelectorAll("button").forEach((b) => b.onclick = () => showStep(M.bags[+b.dataset.bag].first + 1));
  const panel = $("[data-bg-panel]");
  function needList(k) {
    const need = {};
    for (const i of partsInStep[k - 1]) { const key = M.parts[i][0] + "/" + M.parts[i][1]; need[key] = (need[key] || 0) + 1; }
    return Object.entries(need).sort((a, b) => b[1] - a[1]);
  }
  function panelOverview() {
    const saved = store.get();
    panel.innerHTML = `
      <p class="pb-bg__k">The finished model</p>
      <h2 class="pb-bg__ph">${esc(M.meta.overview_title || "Ready when you are")}</h2>
      <p>${esc(M.meta.overview_text || "")}</p>
      <p class="pb-bg__sub">${M.bags.length} bags · ${nSteps} steps · ${M.parts.length.toLocaleString()} pieces</p>
      <div class="pb-bg__row">
        ${saved ? `<button class="pb-bg__btn pb-bg__btn--small pb-bg__btn--primary" type="button" data-go="${saved}">Continue at step ${saved}</button>` : ""}
        <button class="pb-bg__btn pb-bg__btn--small${saved ? "" : " pb-bg__btn--primary"}" type="button" data-go="1">Start at step 1</button>
      </div>
      <div class="pb-bg__need"><h3>Bags</h3><ul class="pb-bg__bagsum">${M.bags.map((b, i) =>
        `<li><b>${String(i + 1).padStart(2, "0")}</b><span>${esc(b.name)}</span><span>${bagParts[i].toLocaleString()}</span></li>`).join("")}</ul></div>`;
    panel.querySelectorAll("[data-go]").forEach((b) => b.onclick = () => showStep(+b.dataset.go));
  }
  function showStep(k, instant) {
    cur = Math.max(0, Math.min(nSteps, k));
    slider.value = cur;
    const s = cur ? M.steps[cur - 1] : null;
    const iso = s && s.sub && s.attach !== s.sub && subIndex[s.sub] !== undefined ? subIndex[s.sub] : -1;
    setCounts(cur || nSteps);
    U.uFocus.value = cur ? 1 : 0;
    U.uIso.value = iso;
    const bag = s ? s.bag : -1;
    rail.querySelectorAll("button").forEach((b, i) => {
      b.classList.toggle("is-done", s ? i < bag : false);
      b.classList.toggle("is-now", i === bag);
    });
    const chips = [];
    if (s) {
      chips.push(`<span class="pb-bg__chip">Step ${cur} / ${nSteps}</span>`);
      if (iso >= 0) chips.push(`<span class="pb-bg__chip pb-bg__chip--off">Built off the model</span>`);
      if (typeof s.attach === "string") chips.push(`<span class="pb-bg__chip pb-bg__chip--off">Fit it on</span>`);
      if (checkpoints.has(cur - 1)) chips.push(`<span class="pb-bg__chip pb-bg__chip--hz">Checkpoint</span>`);
    } else {
      chips.push(`<span class="pb-bg__chip">Finished model</span>`);
    }
    $("[data-bg-badge]").innerHTML = chips.join("");
    $("[data-bg-count]").textContent = cur ? `${placedBefore[cur].toLocaleString()} / ${M.parts.length.toLocaleString()} pieces` : `${M.parts.length.toLocaleString()} pieces`;
    if (!s) {
      panelOverview();
      flyTo(viewFor(modelBox, true), instant);
      return;
    }
    store.set(cur);
    const chunk = s.chunk ? `<span class="pb-bg__sub">Part ${s.chunk[0]} of ${s.chunk[1]}</span>` : "";
    const cp = checkpoints.has(cur - 1) ? `<div class="pb-bg__tip">Checkpoint: everything built so far is one solid piece. Give it a gentle lift to check nothing is loose.</div>` : "";
    panel.innerHTML = `
      <p class="pb-bg__k">Bag ${bag + 1} of ${M.bags.length} · ${esc(M.bags[bag].name)}</p>
      <h2 class="pb-bg__ph">${esc(s.title)}</h2>${chunk}
      <p>${esc(s.text)}</p>
      ${s.tip ? `<div class="pb-bg__tip">${esc(s.tip)}</div>` : ""}${cp}
      <div class="pb-bg__need"><h3>You need</h3><div class="pb-bg__callout">${needList(cur).map(([key, n]) => {
        const [num, cid] = key.split("/");
        return `<div class="pb-bg__pc"><img alt="" width="96" height="96" src="${thumb(num, +cid)}"><b>${n}×</b>${esc(PD[num].name)}<span class="pb-bg__cl"><span class="pb-bg__sw" style="background:${COL[cid].hex}"></span>${esc(COL[cid].name)}</span></div>`;
      }).join("")}</div></div>`;
    let box = stepBox[cur - 1];
    if (iso >= 0) { box = new THREE.Box3(); M.parts.forEach((p, i) => { if (p[7] === s.sub && p[6] < cur) box.union(partBox[i]); }); }
    flyTo(viewFor(box.isEmpty() ? modelBox : box, false), instant);
  }
  const act = (name, fn) => { const b = $(`[data-bg-act="${name}"]`); if (b) b.onclick = fn; return b; };
  act("prev", () => showStep(cur - 1));
  act("next", () => showStep(cur + 1));
  act("all", () => showStep(0));
  act("fit", () => showStep(cur, true));
  const linkBtn = act("link", async () => {
    const path = location.pathname + (cur ? "?step=" + cur : "") + "#pb-bg-build";
    try { await navigator.clipboard.writeText(location.origin + path); linkBtn.textContent = "Link copied"; }
    catch (e) { history.replaceState(null, "", path); linkBtn.textContent = "Link is in the address bar"; }
    setTimeout(() => (linkBtn.textContent = "Copy step link"), 2200);
  });
  const fullBtn = act("full", () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else if (stage.requestFullscreen) stage.requestFullscreen().catch(() => {});
  });
  if (fullBtn && document.fullscreenEnabled && stage.requestFullscreen) fullBtn.hidden = false;
  slider.oninput = (e) => showStep(+e.target.value);
  canvas.addEventListener("keydown", (e) => {
    if (e.key === "ArrowRight") { showStep(cur + 1); e.preventDefault(); }
    if (e.key === "ArrowLeft") { showStep(cur - 1); e.preventDefault(); }
  });
  root.addEventListener("keydown", (e) => {
    if (e.target.closest("input, textarea, button, a") && e.target !== canvas) return;
    if (e.key === "ArrowRight") showStep(cur + 1);
    if (e.key === "ArrowLeft") showStep(cur - 1);
  });
  const goTo = (n) => {
    const st = $("#pb-bg-build");
    if (st) st.scrollIntoView({ behavior: REDUCED ? "auto" : "smooth", block: "start" });
    showStep(n);
  };
  $$("[data-bg-start]").forEach((a) => a.addEventListener("click", (e) => { e.preventDefault(); goTo(store.get() || 1); }));
  $$("[data-bg-go]").forEach((b) => b.addEventListener("click", (e) => { e.preventDefault(); goTo(+b.dataset.bgGo); }));

  function resize() {
    const r = stage.getBoundingClientRect();
    if (!r.width || !r.height) return;
    renderer.setSize(r.width, r.height, false);
    camera.aspect = r.width / r.height;
    camera.updateProjectionMatrix();
    requestRender();
  }
  if ("ResizeObserver" in window) new ResizeObserver(resize).observe(stage); else window.addEventListener("resize", resize);
  document.addEventListener("fullscreenchange", () => { if (fullBtn) fullBtn.textContent = document.fullscreenElement ? "Exit full screen" : "Full screen"; resize(); });
  resize();

  const q = new URLSearchParams(location.search);
  const hashStep = +((location.hash.match(/^#pb-bg-step-(\d+)$/) || [])[1] || 0);
  const start = hashStep || (q.has("step") ? +q.get("step") : 0);
  showStep(start >= 0 && start <= nSteps ? start : 0, true);
  root.classList.add("is-live");
  requestRender();
}
